/** @format */

import {EyeOffIcon} from '@heroicons/react/outline'
import {flexRender, type Cell, type Column} from '@tanstack/react-table'
import clsx from 'clsx'
import React, {useMemo, type PropsWithChildren, type ReactNode} from 'react'

import {useFormApi} from '@src/contexts'

import {SelectionCell} from './SelectionCell'
import {StatusCell} from './StatusCell'
import type {Submission} from './SubmissionsContext'
import {useIsFileUploadArray} from './useIsFileUploadArray'
import {formatDate, getColumnSizeVar, getFileName} from './utils'

type TableCellProps = {
  canExpand?: boolean
  cell: Cell<Submission, unknown>
  // over-limit rows arrive with every column cut short; content cells show
  // the preview muted, and the 'marked' one leads with the redaction icon
  redacted?: boolean | 'marked'
}

export function TableCell(props: TableCellProps) {
  const {canExpand, cell, redacted} = props
  const {id} = cell.column
  const {current: currentForm} = useFormApi()
  // Rows that can't expand are past the retention limit, and sit on the
  // past-limit section's grey field — override the sticky white selection cell.
  const className = canExpand
    ? 'group-hover:bg-gray-50 group-data-[state=expanded]:bg-gray-100'
    : '!bg-gray-100'

  switch (id) {
    case '_selection':
      return (
        <SelectionCell className={className}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </SelectionCell>
      )
    case '_status':
      return (
        <StatusCell className={className}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </StatusCell>
      )
    case '_date':
      return (
        <BaseTableCell
          className={className}
          id={id}
          isLastColumn={cell.column.getIsLastColumn()}
        >
          {formatDate(cell.row.original._date, currentForm.timezone)}
        </BaseTableCell>
      )
    default:
      return (
        <DefaultTableCell
          cell={cell}
          className={className}
          redacted={redacted}
        />
      )
  }
}

type DefaultTableCellProps = {
  cell: Cell<Submission, unknown>
  className: string | undefined
  redacted?: boolean | 'marked'
}

function DefaultTableCell(props: DefaultTableCellProps) {
  const {cell, className, redacted} = props
  const value = cell.getValue()
  const isFileUploadArray = useIsFileUploadArray()

  const d = useMemo(() => {
    if (isFileUploadArray(value)) {
      return value.length === 1
        ? // if there's one file, just show the filename
          getFileName(value[0])
        : `${value.length} files`
    }
    if (typeof value === 'string') {
      return flexRender(cell.column.columnDef.cell, cell.getContext())
    }
    return JSON.stringify(value)
  }, [cell, isFileUploadArray, value]) satisfies ReactNode

  return (
    <BaseTableCell
      className={className}
      id={cell.column.id}
      isLastColumn={cell.column.getIsLastColumn()}
    >
      {redacted ? (
        <span className="text-gray-500">
          {redacted === 'marked' && (
            <EyeOffIcon
              aria-hidden
              className="-mt-0.5 mr-1.5 inline h-4 w-4 text-gray-400"
            />
          )}
          {d}
        </span>
      ) : (
        d
      )}
    </BaseTableCell>
  )
}

type EmptyTableCellProps = {
  column: Column<Submission>
}

export function EmptyTableCell(props: EmptyTableCellProps) {
  const {column} = props
  const {id} = column
  switch (id) {
    case '_selection':
      return (
        <SelectionCell>
          <div className="h-4 w-4" />
        </SelectionCell>
      )
    case '_status':
      return (
        <StatusCell>
          <div className="h-4 w-4" />
        </StatusCell>
      )
    default:
      return (
        <BaseTableCell id={id} isLastColumn={column.getIsLastColumn()}>
          <div className="h-6" />
        </BaseTableCell>
      )
  }
}

type BaseTableCellProps = PropsWithChildren<{
  className?: string
  id: string
  isLastColumn: boolean
}>

function BaseTableCell(props: BaseTableCellProps) {
  const {children, className, id, isLastColumn} = props
  return (
    <div
      className={clsx(
        'flex-none truncate px-4 py-2',
        isLastColumn ? 'grow' : 'border-e border-gray-100',
        className,
      )}
      style={{
        width: `var(${getColumnSizeVar(id)})`,
      }}
    >
      {children}
    </div>
  )
}
